import { observable, computed, makeObservable } from 'mobx'
import { WorkspaceDomainDTO } from '../dtos/domain'
import {
  WorkspaceBrandingDTO,
  WorkspaceConfigDTO,
  BillingConfigDTO,
  WorkspaceCustomMetadataFieldsDTO,
} from 'iam/dtos/workspace-config'
import { WorkspaceDTO } from 'iam/dtos/workspace'
import { Model } from 'infra/model'
import { Id } from 'types/definitions'
import once from 'lodash/once'

export type WorkspaceFeatures = Workspace['features']

export enum WorkspaceClassification {
  B2C = 'B2C',
  B2B = 'B2B',
  B2B2C = 'B2B2C',
  B2E = 'B2E',
  DEMO = 'DEMO',
  FIRM = 'FIRM',
}

export default class Workspace extends Model<WorkspaceDTO, Workspace> {
  @observable id = Id.none
  @observable name = ''
  @observable description = ''
  @observable slug = ''
  @observable archivalStatus: 'ACTIVE' | 'ARCHIVING' | 'ARCHIVED' = 'ACTIVE'
  @observable domains: WorkspaceDomainDTO[] = []
  @observable config: WorkspaceConfigDTO = {}
  @observable customMetadataFields: WorkspaceCustomMetadataFieldsDTO = {}
  @observable branding: WorkspaceBrandingDTO = {
    colors: { primary: '#000000' },
    logos: { primary: '', icon: '', icon_white: '', primary_white: '' },
    corporate: { enabled: false },
    landing_page: {
      image:
        'https://storage.googleapis.com/worklayer-assets/worklayer-homepage/img_login-worklayer1.svg',
    },
  }

  @observable classification: WorkspaceClassification =
    WorkspaceClassification.B2B

  /**
   * Empty workspace instance..
   */
  static get none() {
    return lazyNone()
  }

  constructor() {
    super()
    makeObservable(this)
  }

  /**
   * Parse the workspace.
   */
  parse(attrs: Partial<WorkspaceDTO>): Partial<Workspace> {
    return {
      ...attrs,
      archivalStatus: attrs.archival_status,
      // adding this in case branding is not set for whatever reason
      branding: { ...this.branding, ...attrs.branding },
      customMetadataFields: attrs.custom_metadata_fields,
    }
  }

  toJSON(): WorkspaceDTO {
    return {
      id: this.id || '',
      name: this.name,
      slug: this.slug,
      branding: this.branding,
      config: this.config,
      description: this.description,
      custom_metadata_fields: this.customMetadataFields,
      domains: this.domains,
      archival_status: this.archivalStatus,
    }
  }

  @computed
  get customMetaFields() {
    const metafields = this.customMetadataFields || {}
    return {
      memberMetadataFields: metafields.member_metadata_fields || [],
    }
  }

  @computed
  get features() {
    const cfg = this.config || {}

    return {
      get billing() {
        const billing: BillingConfigDTO = cfg.billing || {}
        const enabled = billing.enabled || false
        const couponsEnabled = (enabled && billing['coupons.enabled']) || false
        const couponsReferralEnabled =
          (enabled && couponsEnabled && billing['coupons.referral.enabled']) ||
          false
        const teamsCreditEnabled =
          (enabled && billing['teams.credit.enabled']) || false
        const teamsCreditLimit =
          (enabled &&
            billing['teams.credit.enabled'] &&
            billing['teams.credit.limit']) ||
          0
        return {
          enabled,
          couponsEnabled,
          couponsReferral: {
            enabled: couponsReferralEnabled,
            amount: billing['coupons.referral.amount'] || 0,
          },
          teamsCredit: {
            enabled: teamsCreditEnabled,
            limit: teamsCreditLimit,
          },
          stripe: {
            publicKey: billing.stripe_pk,
          },
        }
      },
      legendsBuilderConfig: {
        enabled:
          (cfg.legends_builder_config && cfg.legends_builder_config.enabled) ||
          false,
      },
      sureprep: {
        enabled: (cfg.sureprep && cfg.sureprep.enabled) || false,
      },
      impersonation: {
        enabled: (cfg.impersonation && cfg.impersonation.enabled) || false,
      },
      tos: {
        showOnLogin: (cfg.tos && cfg.tos?.show_on_login) || false,
        hideOnWortal: (cfg.tos && cfg.tos?.hide_on_wortal) || false,
        customTermsEnabled:
          (cfg.tos &&
            cfg.tos?.custom_source !== undefined &&
            cfg.tos?.custom_source !== null) ||
          false,
      },
      pp: { enabled: (cfg.pp && cfg.pp.enabled) || false },
      analyticsConfig: {
        enabled:
          (cfg.analytics_config && cfg.analytics_config.enabled) || false,
      },
      jobConfig: {
        maxConcurrentProviderJobs:
          (cfg.job_config && cfg.job_config.max_concurrent_provider_jobs) || 0,
        providerCompletedJobAccess:
          (cfg.job_config && cfg.job_config.provider_completed_job_access) ||
          'NO_ACCESS',
        anonymousOpportunities:
          (cfg.job_config && cfg.job_config.anonymous_opportunities) || false,
        jobProgressionConfig:
          (cfg.job_config && cfg.job_config.job_progression_flow) || false,
      },
      jobProgressionConfig: {
        enabled:
          (cfg.job_progression_config && cfg.job_progression_config.enabled) ||
          false,
        legends:
          (cfg.job_progression_config && cfg.job_progression_config.legends) ||
          {},
      },
      documentConfig: {
        documentUploadConfirmation: {
          enabled:
            (cfg.document_config &&
              cfg.document_config.document_upload_confirmation &&
              cfg.document_config.document_upload_confirmation.enabled) ||
            false,
          text:
            (cfg.document_config &&
              cfg.document_config.document_upload_confirmation &&
              cfg.document_config.document_upload_confirmation.text) ||
            '',
        },
        documentEditPermission:
          (cfg.document_config &&
            cfg.document_config.document_edit_permission) ||
          null,
      },
      messageConfig: {
        jobTaxAssistant: {
          enabled: Boolean(cfg.message_config?.tax_assistant?.enabled),
        },
        welcomeMessage: {
          enabled:
            (cfg.message_config &&
              cfg.message_config.welcome_message &&
              cfg.message_config.welcome_message.enabled) ||
            false,
          text:
            (cfg.message_config &&
              cfg.message_config.welcome_message &&
              cfg.message_config.welcome_message.text) ||
            '',
        },
      },
      combinedPricingConfig: {
        jobScopeChangeRequestsEnabled: Boolean(
          cfg.combined_pricing &&
            cfg.combined_pricing?.job_scope_change_requests_enabled,
        ),
        jobScopeRequireClientApproval: Boolean(
          cfg.combined_pricing &&
            cfg.combined_pricing?.job_scope_change_requests_enabled &&
            cfg.combined_pricing?.job_scope_require_client_approval,
        ),
        jobScopePromptClientApproval: Boolean(
          cfg.combined_pricing &&
            cfg.combined_pricing?.job_scope_change_requests_enabled &&
            cfg.combined_pricing?.job_scope_require_client_approval &&
            cfg.combined_pricing?.job_scope_prompt_client_approval,
        ),
        legends: cfg.combined_pricing && cfg.combined_pricing?.legends,
      },
      messageBankConfig: {
        enabled: cfg.message_bank_config?.enabled ?? false,
      },
      payoutsConfig: {
        enabled: Boolean(cfg.payouts?.enabled),
      },
      notificationsConfig: {
        allowSnoozing: cfg.notifications_config?.allow_snoozing ?? false,
        JobClosed:
          cfg.notifications_config &&
          (cfg.notifications_config.JobClosed === true ||
            cfg.notifications_config.JobClosed === false)
            ? cfg.notifications_config.JobClosed
            : true,
        JobReopened:
          cfg.notifications_config &&
          (cfg.notifications_config.JobReopened === true ||
            cfg.notifications_config.JobReopened === false)
            ? cfg.notifications_config.JobReopened
            : true,
        JobUpdated:
          cfg.notifications_config &&
          (cfg.notifications_config.JobUpdated === true ||
            cfg.notifications_config.JobUpdated === false)
            ? cfg.notifications_config.JobUpdated
            : true,
        MilestoneCompleted:
          cfg.notifications_config &&
          (cfg.notifications_config.MilestoneCompleted === true ||
            cfg.notifications_config.MilestoneCompleted === false)
            ? cfg.notifications_config.MilestoneCompleted
            : true,
        NewJobAvailable:
          cfg.notifications_config &&
          (cfg.notifications_config.NewJobAvailable === true ||
            cfg.notifications_config.NewJobAvailable === false)
            ? cfg.notifications_config.NewJobAvailable
            : true,
        UnreadMessage:
          cfg.notifications_config &&
          (cfg.notifications_config.UnreadMessage === true ||
            cfg.notifications_config.UnreadMessage === false)
            ? cfg.notifications_config.UnreadMessage
            : true,
        UnreadDocument:
          cfg.notifications_config &&
          (cfg.notifications_config.UnreadDocument === true ||
            cfg.notifications_config.UnreadDocument === false)
            ? cfg.notifications_config.UnreadDocument
            : true,
      },
      documentSignatureConfig: {
        enabled: cfg.document_signature_config?.enabled ?? false,
      },
      roomsConfig: {
        enabled: cfg.rooms_config?.enabled ?? false,
      },
    }
  }
}

const lazyNone = once(() =>
  new Workspace().set({
    name: 'Worklayer',
    description: 'Worklayer Work Portal',
  }),
)
